/**
 * ChoiceOverlay - 選擇系統 UI 覆蓋層
 * 顯示選項選擇、卡牌選擇等 UI
 */

import { useState, useCallback } from 'react';
import { useChoiceSystem } from '../hooks/useChoiceSystem';
import { useGameStore } from '../../store/gameStore';
import type { Card, CardDefinition } from '../../types';

// ============================================================
// 樣式
// ============================================================

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: '#1a1a2e',
    borderRadius: '12px',
    padding: '24px',
    minWidth: '400px',
    maxWidth: '80vw',
    maxHeight: '80vh',
    overflow: 'auto',
    border: '2px solid #4a4a6a',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
  },
  header: {
    marginBottom: '16px',
    textAlign: 'center' as const,
  },
  title: {
    color: '#ffd700',
    fontSize: '24px',
    fontWeight: 'bold',
    margin: '0 0 8px 0',
  },
  subtitle: {
    color: '#aaa',
    fontSize: '14px',
    margin: 0,
  },
  effectText: {
    color: '#88ccff',
    fontSize: '16px',
    textAlign: 'center' as const,
    padding: '12px',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    borderRadius: '8px',
    marginBottom: '20px',
  },
  optionsContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '12px',
  },
  optionButton: {
    padding: '16px 24px',
    fontSize: '18px',
    backgroundColor: '#2a2a4e',
    color: '#fff',
    border: '2px solid #4a4a6a',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    textAlign: 'left' as const,
  },
  optionButtonHover: {
    backgroundColor: '#3a3a5e',
    borderColor: '#ffd700',
  },
  optionButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  cardsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: '12px',
    maxHeight: '400px',
    overflowY: 'auto' as const,
    padding: '8px',
  },
  cardItem: {
    position: 'relative' as const,
    aspectRatio: '0.7',
    borderRadius: '8px',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    border: '2px solid transparent',
  },
  cardItemSelected: {
    border: '2px solid #ffd700',
    boxShadow: '0 0 12px rgba(255, 215, 0, 0.5)',
    transform: 'scale(1.05)',
  },
  cardImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
  },
  cardInfo: {
    position: 'absolute' as const,
    bottom: 0,
    left: 0,
    right: 0,
    padding: '8px',
    background: 'linear-gradient(transparent, rgba(0,0,0,0.9))',
    color: '#fff',
    fontSize: '12px',
  },
  cardName: {
    fontWeight: 'bold',
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  cardStats: {
    color: '#aaa',
    fontSize: '11px',
  },
  footer: {
    marginTop: '20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  selectionInfo: {
    color: '#aaa',
    fontSize: '14px',
  },
  buttonGroup: {
    display: 'flex',
    gap: '12px',
  },
  confirmButton: {
    padding: '12px 24px',
    fontSize: '16px',
    backgroundColor: '#4a9',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
  confirmButtonDisabled: {
    backgroundColor: '#666',
    cursor: 'not-allowed',
  },
  cancelButton: {
    padding: '12px 24px',
    fontSize: '16px',
    backgroundColor: '#a44',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  targetHint: {
    position: 'fixed' as const,
    bottom: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    color: '#ffd700',
    padding: '12px 24px',
    borderRadius: '8px',
    fontSize: '16px',
    zIndex: 1001,
    border: '2px solid #ffd700',
  },
};

// ============================================================
// 子組件
// ============================================================

interface OptionChoiceProps {
  options: Array<{ id: string; label: string; description?: string; disabled?: boolean; disabledReason?: string }>;
  onSelect: (optionId: string) => void;
}

function OptionChoice({ options, onSelect }: OptionChoiceProps) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  return (
    <div style={styles.optionsContainer}>
      {options.map((option, index) => (
        <button
          key={option.id}
          style={{
            ...styles.optionButton,
            ...(hoveredId === option.id && !option.disabled ? styles.optionButtonHover : {}),
            ...(option.disabled ? styles.optionButtonDisabled : {}),
          }}
          onClick={() => { if (!option.disabled) onSelect(option.id); }}
          onMouseEnter={() => { setHoveredId(option.id); }}
          onMouseLeave={() => { setHoveredId(null); }}
          disabled={option.disabled}
          title={option.disabledReason}
        >
          <span style={{ marginRight: '12px', color: '#ffd700' }}>{index + 1})</span>
          {option.label}
          {option.description && (
            <div style={{ fontSize: '14px', color: '#888', marginTop: '4px' }}>
              {option.description}
            </div>
          )}
        </button>
      ))}
    </div>
  );
}

interface CardChoiceProps {
  cards: Card[];
  definitions: Map<string, CardDefinition>;
  minCount: number;
  maxCount: number;
  onConfirm: (cardIds: string[]) => void;
  onCancel?: () => void;
  canCancel?: boolean;
}

function CardChoice({ cards, definitions, minCount, maxCount, onConfirm, onCancel, canCancel }: CardChoiceProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggleCard = useCallback((cardId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(cardId)) {
        next.delete(cardId);
      } else if (next.size < maxCount) {
        next.add(cardId);
      }
      return next;
    });
  }, [maxCount]);

  const canConfirm = selectedIds.size >= minCount && selectedIds.size <= maxCount;

  return (
    <>
      <div style={styles.cardsContainer}>
        {cards.map(card => {
          const def = definitions.get(card.definitionId);
          const isSelected = selectedIds.has(card.id);
          const imagePath = def?.imagePath ?? '/placeholder-card.png';

          return (
            <div
              key={card.id}
              style={{
                ...styles.cardItem,
                ...(isSelected ? styles.cardItemSelected : {}),
              }}
              onClick={() => { toggleCard(card.id); }}
            >
              <img
                src={imagePath}
                alt={def?.nameZh ?? card.id}
                style={styles.cardImage}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = '/placeholder-card.png';
                }}
              />
              <div style={styles.cardInfo}>
                <div style={styles.cardName}>{def?.nameZh ?? card.id}</div>
                {def?.type === 'unit' && (
                  <div style={styles.cardStats}>
                    ATK: {(def.atk ?? 0) + (card.atkModifier ?? 0)}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div style={styles.footer}>
        <span style={styles.selectionInfo}>
          已選擇 {selectedIds.size}/{maxCount} 張
          {minCount > 0 && ` (至少 ${String(minCount)} 張)`}
        </span>
        <div style={styles.buttonGroup}>
          {canCancel && (
            <button style={styles.cancelButton} onClick={onCancel}>
              取消
            </button>
          )}
          <button
            style={{
              ...styles.confirmButton,
              ...(!canConfirm ? styles.confirmButtonDisabled : {}),
            }}
            onClick={() => { if (canConfirm) onConfirm(Array.from(selectedIds)); }}
            disabled={!canConfirm}
          >
            確認
          </button>
        </div>
      </div>
    </>
  );
}

// ============================================================
// 主組件
// ============================================================

export function ChoiceOverlay() {
  const {
    pendingChoice,
    hasPendingChoice,
    validTargets,
    selectOption,
    selectCards,
    cancel,
    getChoiceTypeLabel,
  } = useChoiceSystem();

  const cardDefinitions = useGameStore(state => state.cardDefinitions);

  // 如果沒有待決選擇，不渲染
  if (!hasPendingChoice || !pendingChoice) {
    return null;
  }

  // 目標選擇類型：不顯示 modal，只顯示提示
  if (pendingChoice.type === 'target') {
    return (
      <div style={styles.targetHint}>
        {pendingChoice.effectText}
        <br />
        <span style={{ fontSize: '14px', color: '#aaa' }}>
          點擊戰場上高亮的目標來選擇 ({pendingChoice.minCount ?? 1} 個)
        </span>
        {pendingChoice.optional && (
          <button
            style={{ ...styles.cancelButton, marginLeft: '16px', padding: '8px 16px' }}
            onClick={cancel}
          >
            跳過
          </button>
        )}
      </div>
    );
  }

  // 選項選擇
  if (pendingChoice.type === 'option' && pendingChoice.options) {
    return (
      <div style={styles.overlay}>
        <div style={styles.modal}>
          <div style={styles.header}>
            <h2 style={styles.title}>{getChoiceTypeLabel()}</h2>
            <p style={styles.subtitle}>{pendingChoice.sourceCardName}</p>
          </div>
          <div style={styles.effectText}>{pendingChoice.effectText}</div>
          <OptionChoice
            options={pendingChoice.options}
            onSelect={selectOption}
          />
        </div>
      </div>
    );
  }

  // 卡牌選擇
  if (pendingChoice.type === 'card') {
    return (
      <div style={styles.overlay}>
        <div style={styles.modal}>
          <div style={styles.header}>
            <h2 style={styles.title}>{getChoiceTypeLabel()}</h2>
            <p style={styles.subtitle}>{pendingChoice.sourceCardName}</p>
          </div>
          <div style={styles.effectText}>{pendingChoice.effectText}</div>
          <CardChoice
            cards={validTargets}
            definitions={cardDefinitions}
            minCount={pendingChoice.minCount ?? 1}
            maxCount={pendingChoice.maxCount ?? 1}
            onConfirm={selectCards}
            onCancel={pendingChoice.optional ? cancel : undefined}
            canCancel={pendingChoice.optional}
          />
        </div>
      </div>
    );
  }

  // 其他類型暫不處理
  return null;
}

export default ChoiceOverlay;
